import React, { useState, useRef, useEffect } from "react";
import CropControls from "../../../components/CropControl/CropControl";
import FileUpload from "../../../components/Inputs/FileUpload/FileUpload";
import styles from "./CreateBooster.module.css";
import ColorSelector from "../../../components/Inputs/ColorSelector/ColorSelector";
import defaultbooster from "../../../assets/cardBack.png";
import {
  GenerateBoosterData,
  boosterData,
  generateBooster,
  updateBooster,
} from "../../../services/BoosterServices";
import { useCreatorBoostersContext } from "../../../context/Creator/CreatorBoostersProvider";
import { useNavigate } from "react-router-dom";
import globalStyles from "../../../styles/globalStyles.module.css";
import LoadingIcon from "../../CssIcon/Loading/LoadingIcon";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../context/AlertProvider";

type BoosterDataProps = {
  booster?: boosterData;
};

const CreateOrUpdateBooster: React.FC<BoosterDataProps> = ({ booster }) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>(
    booster?.original_image_id || defaultbooster
  );
  const [borderColor, setBorderColor] = useState<string>("#FFFFFF");
  const [boosterName, setBoosterName] = useState<string>(booster?.name || "");
  // const [usernameCredit, setUsernameCredit] = useState<string>(
  //   "Designed by Cardonize"
  // );
  const storedUser = localStorage.getItem("user");
  var usernameCredit = storedUser ? "@" + JSON.parse(storedUser).username : "";

  const [boosterDescription, setBoosterDescription] = useState<string>(
    booster?.description || ""
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [boosterWidth, setBoosterWidth] = useState<number>(0);
  const [cropData, setCropData] = useState<{
    offset: { x: number; y: number };
    scale: number;
  }>({ offset: { x: 0, y: 0 }, scale: 0 });
  const { boostersState, setFilteredBoosters } = useCreatorBoostersContext();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    if (booster?.is_in_store) {
      alert("You cannot modify a booster in the store");
      alert("Please remove the booster from the store before first");
      navigate(-1);
    }

    const mediaQuery = window.matchMedia("(max-width: 800px)");
    var resizeValue = 0.23;

    if (mediaQuery.matches) {
      resizeValue = 0.7;
    }

    const updateBoosterWidth = () => {
      const width = window.innerWidth * resizeValue;
      setBoosterWidth(width);
    };

    updateBoosterWidth();

    window.addEventListener("resize", updateBoosterWidth);

    return () => {
      window.removeEventListener("resize", updateBoosterWidth);
    };
  }, [booster, navigate]);

  const cropContainerRef = useRef<HTMLDivElement>(null);

  const handleFileChange = (file: File) => {
    setImageFile(file);
    const previewURL = URL.createObjectURL(file);
    setImageSrc(previewURL);
  };

  const handleBorderColorChange = (newBorderColor: string) =>
    setBorderColor(newBorderColor);

  const handleBoosterDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => setBoosterDescription(e.target.value);

  const handleSaveBooster = async () => {
    if (!imageFile && !booster?.image_id) {
      showAlert("alert.boosters.create.error.noFileProvided");
      return;
    }

    if (boosterName === "") {
      showAlert(t("alert.boosters.create.error.noName"));
      return;
    }

    setIsUploading(true);

    if (!cropContainerRef.current) return;

    try {
      var imageBlob;
      if (!imageFile) {
        const response = await fetch(imageSrc);
        imageBlob = await response.blob();
      } else {
        imageBlob = new Blob([imageFile], { type: imageFile.type });
      }

      const dataToSave: Omit<
        GenerateBoosterData,
        | "id"
        | "created_at"
        | "updated_at"
        | "image_id"
        | "original_image_id"
        | "price"
        | "is_in_store"
        | "masterset_card_id"
        | "status"
      > = {
        name: boosterName,
        description: boosterDescription as string,
        image_file: imageBlob,
        offset_x: cropData.offset.x,
        offset_y: cropData.offset.y,
        width: boosterWidth,
        zoom: cropData.scale,
        border_color: borderColor,
        username: usernameCredit,
      };

      let savedBooster: boosterData;
      if (booster?.id) {
        savedBooster = await updateBooster(booster.id, dataToSave);
      } else {
        savedBooster = await generateBooster(dataToSave);
      }

      boostersState.setData((prev) =>
        booster?.id
          ? prev.map((b) => (b.id === booster.id ? savedBooster : b))
          : [savedBooster, ...prev]
      );
      setFilteredBoosters((prev) =>
        booster?.id
          ? prev.map((b) => (b.id === booster.id ? savedBooster : b))
          : [savedBooster, ...prev]
      );

      navigate(`/creator/boosters/${savedBooster.id}/cards`);
    } catch (error) {
      showAlert(t("alert.boosters.create.error.default"));

      console.error("Failed to save booster:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropData = (
    newOffset: { x: number; y: number },
    newScale: number
  ) => {
    setCropData({
      offset: newOffset,
      scale: newScale,
    });
  };

  return (
    <>
      <div className={globalStyles.pageTitle}>
        {booster ? "Update Booster" : t("boosters.title")}
      </div>
      {isUploading && <LoadingIcon />}

      <div className={styles.container}>
        <div className={styles.inputContainer}>
          <div>
            <FileUpload onFileChange={(file: File) => handleFileChange(file)} />
            <div className="d-none">
              <ColorSelector
                imageSrc={imageSrc}
                color={borderColor}
                onColorChange={handleBorderColorChange}
                showBorderless={false}
              />
            </div>
            <div className={styles.inputWithLabel}>
              <label>{t("name")}</label>
              <input
                type="text"
                value={boosterName}
                onChange={(e) => setBoosterName(e.target.value)}
                className={globalStyles.input}
                maxLength={40}
              />
            </div>
            <div className={styles.inputWithLabel}>
              <label>{t("description")}</label>
              <textarea
                value={boosterDescription}
                onChange={handleBoosterDescriptionChange}
                className={globalStyles.input}
                maxLength={255}
              />
            </div>
          </div>
          <button
            onClick={handleSaveBooster}
            className={`${globalStyles.button} ${styles.createboosterButton}`}
            disabled={isUploading}
          >
            {isUploading ? t("uploading") : booster ? t("update") : t("create")}
          </button>
        </div>

        <div
          className={styles.cropContainer}
          ref={cropContainerRef}
          style={{
            width: boosterWidth + "px",
            height: boosterWidth * 1.8 + "px",
            fontSize: boosterWidth / 600 + "px",
          }}
        >
          <img
            src="/images/boosters/boosterFrame.svg"
            style={{
              width: boosterWidth + "px",
              height: boosterWidth * 1.8 + "px",

              position: "absolute",
              zIndex: 2,
              pointerEvents: "none",
            }}
            alt="boosterFrame"
          />
          <div className={styles.boosterNameInput}>{boosterName}</div>
          <div className={styles.usernameCredit}>{usernameCredit}</div>
          <div className={styles.cropControlZone}>
            <CropControls imageSrc={imageSrc} handleCropData={handleCropData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrUpdateBooster;
